/**
 * Analytics API service.
 *
 * Centralizes all analytics-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module analyticsApi
 */

import { apiGet } from './apiUtils';

/**
 * Fetch questions analytics data
 *
 * @param {Record<string, string>} filters - Optional filters for analytics
 * @returns {Promise<any>} Analytics data
 */
export const getQuestionsAnalytics = async (
  filters: Record<string, string> = {},
): Promise<any> => {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && value !== 'all') {
      queryParams.append(key, value);
    }
  });

  const query = queryParams.toString();
  const url = query
    ? `/admin/analytics/questions?${query}`
    : '/admin/analytics/questions';

  const result = await apiGet(url, {
    errorMessage: 'Failed to load questions analytics',
    context: 'getQuestionsAnalytics',
    throwOnError: true,
  });
  if (result === null) {
    throw new Error('Failed to load questions analytics');
  }
  return result;
};

/**
 * Fetch question performance data
 *
 * @param {Record<string, string | number>} params - Query parameters (page, limit, sortBy, filters)
 * @returns {Promise<any>} Performance data with pagination
 */
export const getQuestionPerformance = async (
  params: Record<string, string | number> = {},
): Promise<any> => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== 'all') {
      queryParams.append(key, String(value));
    }
  });

  const query = queryParams.toString();
  const url = query
    ? `/admin/analytics/question-performance?${query}`
    : '/admin/analytics/question-performance';

  const result = await apiGet(url, {
    errorMessage: 'Failed to load question performance data',
    context: 'getQuestionPerformance',
    throwOnError: true,
  });
  if (result === null) {
    throw new Error('Failed to load question performance data');
  }
  return result;
};
